import { useState, useCallback } from 'react'
import { Streamdown } from 'streamdown'
import 'streamdown/styles.css'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { InlineImageToolbarButton } from './inline-image-toolbar-button'
import type { UseFormRegister, UseFormWatch, FieldPath } from 'react-hook-form'
import type { DraftEditFormData } from '@/lib/constants/draft'

interface DraftBodyEditorProps<T extends { body: string } = DraftEditFormData> {
  register: UseFormRegister<T>
  watch: UseFormWatch<T>
  error?: string
  onInsertImage?: () => void
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>
}

export function DraftBodyEditor<T extends { body: string } = DraftEditFormData>({
  register,
  watch,
  error,
  onInsertImage,
  textareaRef,
}: DraftBodyEditorProps<T>) {
  const [activeTab, setActiveTab] = useState<string>('edit')
  const bodyPath = 'body' as FieldPath<T>
  const bodyContent = watch(bodyPath) as string

  const { ref: formRef, ...bodyRest } = register(bodyPath)
  const mergedRef = useCallback((el: HTMLTextAreaElement | null) => {
    formRef(el)
    if (textareaRef) (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el
  }, [formRef, textareaRef])

  return (
    <Card>
      <CardContent className="p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b border-border px-4 pt-2">
            <TabsList variant="line" className="gap-1">
              <TabsTrigger value="edit" className="text-sm">Edit</TabsTrigger>
              <TabsTrigger value="preview" className="text-sm">Preview</TabsTrigger>
            </TabsList>
          </div>
          {onInsertImage && (
            <div className="flex items-center gap-1 border-b border-border px-4 py-1">
              <InlineImageToolbarButton onClick={onInsertImage} />
            </div>
          )}
          <TabsContent value="edit" className="p-4">
            <Textarea
              ref={mergedRef}
              {...bodyRest}
              className="font-mono min-h-[400px] resize-y"
              placeholder="Write your blog post in markdown..."
            />
            {error && <p className="text-sm text-destructive mt-1">{error}</p>}
          </TabsContent>
          <TabsContent value="preview" className="p-6">
            <article role="article" className="prose prose-sm max-w-none">
              {bodyContent ? (
                <Streamdown>{bodyContent}</Streamdown>
              ) : (
                <p className="text-muted-foreground">Nothing to preview yet.</p>
              )}
            </article>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
